import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from 'components/ui/button';

export interface ReferentialPageHeaderProps {
  /** Optional: the technician record shows its back button here instead (`leadingAction`). */
  icon?: LucideIcon;
  title: string;
  subtitle: string;
  /** Label of the create action. Omitted for an account that may view but not manage. */
  actionLabel?: string;
  onAction?: () => void;
  /** Rendered left of the primary action — an import, an export, a secondary route in. */
  secondaryAction?: ReactNode;
  /** First child of the title row — the record's 40 px back button. */
  leadingAction?: ReactNode;
  /** Inline after the H1 — the record's status dot and its edit-mode pill. */
  titleBadges?: ReactNode;
}

/**
 * Page header of both referential screens: icon, H1, subtitle, and the create action on the
 * right. The action lives here and not in the table card — the design puts it in the header, and
 * a table that scrolls should not carry the primary action out of view with it.
 */
const ReferentialPageHeader = ({
  icon: Icon,
  title,
  subtitle,
  actionLabel,
  onAction,
  secondaryAction,
  leadingAction,
  titleBadges,
}: ReferentialPageHeaderProps) => (
  <div className="flex flex-wrap items-start justify-between gap-3">
    <div className="flex items-center gap-3">
      {/* Opt-in slots rather than a second header component: one row, one set of paddings to keep. */}
      {leadingAction}
      {Icon && (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800">
          <Icon className="h-5 w-5 text-zinc-700 dark:text-zinc-200" aria-hidden="true" />
        </span>
      )}
      <div>
        <h1 className="flex flex-wrap items-center gap-2.5 text-xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          {title}
          {titleBadges}
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400">{subtitle}</p>
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {secondaryAction}
      {actionLabel && onAction && (
        <Button onClick={onAction}>
          <Plus className="mr-1 h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </div>
  </div>
);

export default ReferentialPageHeader;
